import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import { alpha, SxProps, Theme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ReactElement, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { trackEvent } from 'src/hooks/useAppInsights'
import { RootState } from 'src/redux/stores/store'

import { handleHelpMe } from '../utils'

const LSU_LINK = 'https://learn.lennar.com/learn/learning-plans/38/plan-library'

export const SEND_HELP_ME_REQUEST_LABEL = 'Send Help Me Request'
export const LSU_TRAINING_LABEL = 'LSU Training'
export const OPEN_LSU_TRAINING_LABEL = 'Open LSU Training'
export const NEED_A_HAND_LABEL = 'Need a Hand?'

/** Height of both header controls, labelled or icon-only. */
const CONTROL_HEIGHT = 38
/** Below this the header row runs out of room, so the labels give way to icons. */
const LABELLED_HEADER_MIN_WIDTH = 1280
/** Background only, per the design: no transform, no scale. */
const TRANSITION = 'background-color 200ms cubic-bezier(0.2, 0.6, 0.2, 1)'
const FOCUS_RING_OPACITY = 0.35
const HEADER_ICON_SIZE = 18
const NAV_ICON_SIZE = 20
const EYEBROW_ICON_SIZE = 16
/** Keeps the full-width nav rows above the 44px minimum touch target. */
const NAV_ROW_MIN_HEIGHT = 44

const SUPPORT_ACTION_EVENT_NAME = 'SupportAction'

type SupportActionSource = 'header' | 'nav'

interface SupportActionsProps {
  variant: SupportActionSource
}

const cobaltBlue = (theme: Theme): string =>
  theme.lennarDesignSystem.lennarBlues.lennarBlue

const cobaltTint = (theme: Theme): string =>
  theme.lennarDesignSystem.lennarBlueTints.cobaltTint

const oceanBlue = (theme: Theme): string =>
  theme.lennarDesignSystem.lennarBlues.oceanBlue

const white = (theme: Theme): string => theme.lennarDesignSystem.neutrals.white

const focusRing = (theme: Theme): string =>
  `0 0 0 3px ${alpha(cobaltBlue(theme), FOCUS_RING_OPACITY)}`

const headerControlSx: SxProps<Theme> = {
  '&:active': { transform: 'translateY(1px)' },
  '&:focus-visible': { boxShadow: focusRing },
  borderRadius: 0.5,
  height: CONTROL_HEIGHT,
  minWidth: 0,
  transition: TRANSITION,
}

const primaryControlSx: SxProps<Theme> = {
  '&:hover': { backgroundColor: oceanBlue },
  backgroundColor: cobaltBlue,
  color: white,
}

const secondaryControlSx: SxProps<Theme> = {
  '&:hover': { backgroundColor: cobaltTint },
  backgroundColor: white,
  border: 1,
  borderColor: cobaltBlue,
  color: cobaltBlue,
}

const headerButtonSx: SxProps<Theme> = {
  ...headerControlSx,
  '& .MuiButton-startIcon': { marginRight: 1 },
  '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: HEADER_ICON_SIZE },
  fontSize: 15,
  fontWeight: 600,
  px: 2,
  textTransform: 'none',
  whiteSpace: 'nowrap',
}

const iconOnlyControlSx: SxProps<Theme> = {
  ...headerControlSx,
  width: CONTROL_HEIGHT,
}

const navRowSx: SxProps<Theme> = {
  '&:hover': { backgroundColor: cobaltTint },
  color: cobaltBlue,
  gap: 1.5,
  minHeight: NAV_ROW_MIN_HEIGHT,
  px: 2.25,
  py: 1.5,
  transition: TRANSITION,
}

const navRowIconSx: SxProps<Theme> = { color: 'inherit', minWidth: 0 }

const navRowTextProps = { fontSize: 16, fontWeight: 500 }

const eyebrowSx: SxProps<Theme> = {
  alignItems: 'center',
  backgroundColor: 'transparent',
  color: cobaltBlue,
  display: 'flex',
  fontSize: 11,
  fontWeight: 700,
  gap: 1,
  letterSpacing: '0.18em',
  lineHeight: 1,
  pb: 1,
  pt: 2,
  px: 2.25,
  textTransform: 'uppercase',
}

interface SupportActionHandlers {
  handleOpenTrainingLink: () => void
  handleSendHelpMeRequest: () => void
}

/**
 * Both placements run the same handlers against the same destinations; only the
 * analytics source differs, so adoption can be measured per placement.
 */
const useSupportActionHandlers = (
  source: SupportActionSource
): SupportActionHandlers => {
  const trackSupportAction = useCallback(
    (action: string) => {
      trackEvent({ name: SUPPORT_ACTION_EVENT_NAME }, { action, source })
    },
    [source]
  )

  const handleSendHelpMeRequest = useCallback(() => {
    trackSupportAction(SEND_HELP_ME_REQUEST_LABEL)
    handleHelpMe()
  }, [trackSupportAction])

  const handleOpenTrainingLink = useCallback(() => {
    trackSupportAction(LSU_TRAINING_LABEL)
    window.open(LSU_LINK, '_blank', 'noopener,noreferrer')
  }, [trackSupportAction])

  return { handleOpenTrainingLink, handleSendHelpMeRequest }
}

const HeaderSupportActions: React.FC = () => {
  const { handleOpenTrainingLink, handleSendHelpMeRequest } =
    useSupportActionHandlers('header')
  const areLabelsVisible = useMediaQuery(
    `(min-width:${LABELLED_HEADER_MIN_WIDTH}px)`
  )

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
      {areLabelsVisible ? (
        <>
          <Button
            disableElevation
            startIcon={<MailOutlineIcon />}
            onClick={handleSendHelpMeRequest}
            sx={{ ...headerButtonSx, ...primaryControlSx }}
          >
            {SEND_HELP_ME_REQUEST_LABEL}
          </Button>
          <Button
            disableElevation
            startIcon={<SchoolOutlinedIcon />}
            onClick={handleOpenTrainingLink}
            sx={{ ...headerButtonSx, ...secondaryControlSx }}
          >
            {LSU_TRAINING_LABEL}
          </Button>
        </>
      ) : (
        <>
          <Tooltip title={SEND_HELP_ME_REQUEST_LABEL}>
            <IconButton
              aria-label={SEND_HELP_ME_REQUEST_LABEL}
              onClick={handleSendHelpMeRequest}
              sx={{ ...iconOnlyControlSx, ...primaryControlSx }}
            >
              <MailOutlineIcon sx={{ fontSize: HEADER_ICON_SIZE }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={LSU_TRAINING_LABEL}>
            <IconButton
              aria-label={LSU_TRAINING_LABEL}
              onClick={handleOpenTrainingLink}
              sx={{ ...iconOnlyControlSx, ...secondaryControlSx }}
            >
              <SchoolOutlinedIcon sx={{ fontSize: HEADER_ICON_SIZE }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  )
}

interface NavSupportActionRowProps {
  icon: ReactElement
  isLabelVisible: boolean
  label: string
  onClick: () => void
}

const NavSupportActionRow: React.FC<NavSupportActionRowProps> = ({
  icon,
  isLabelVisible,
  label,
  onClick,
}) => (
  <ListItemButton aria-label={label} onClick={onClick} sx={navRowSx}>
    <ListItemIcon sx={navRowIconSx}>
      <Tooltip title={label}>{icon}</Tooltip>
    </ListItemIcon>
    {isLabelVisible && (
      <ListItemText primary={label} primaryTypographyProps={navRowTextProps} />
    )}
  </ListItemButton>
)

const NavSupportActions: React.FC = () => {
  const { handleOpenTrainingLink, handleSendHelpMeRequest } =
    useSupportActionHandlers('nav')
  // On the collapsed rail the eyebrow and the row labels both give way to
  // icons; the tooltips carry the labels and the divider keeps the two rows
  // reading as a group.
  const isDrawerOpen = useSelector((state: RootState) => state.drawer.open)

  return (
    <>
      <Divider sx={{ mt: 2.25, mx: 2.25 }} />
      <List
        component="nav"
        aria-label={NEED_A_HAND_LABEL}
        disablePadding
        subheader={
          isDrawerOpen ? (
            <ListSubheader component="div" sx={eyebrowSx}>
              <HelpOutlineIcon sx={{ fontSize: EYEBROW_ICON_SIZE }} />
              {NEED_A_HAND_LABEL}
            </ListSubheader>
          ) : undefined
        }
      >
        <NavSupportActionRow
          icon={<MailOutlineIcon sx={{ fontSize: NAV_ICON_SIZE }} />}
          isLabelVisible={isDrawerOpen}
          label={SEND_HELP_ME_REQUEST_LABEL}
          onClick={handleSendHelpMeRequest}
        />
        <NavSupportActionRow
          icon={<SchoolOutlinedIcon sx={{ fontSize: NAV_ICON_SIZE }} />}
          isLabelVisible={isDrawerOpen}
          label={OPEN_LSU_TRAINING_LABEL}
          onClick={handleOpenTrainingLink}
        />
      </List>
    </>
  )
}

const SupportActions: React.FC<SupportActionsProps> = ({ variant }) =>
  variant === 'header' ? <HeaderSupportActions /> : <NavSupportActions />

export default SupportActions
